import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '../index';

export interface NotificationAction {
    id: string;
    type: 'toast_click' | 'primary' | 'secondary';
    style?: 'invisible' | 'default';
    label?: Record<string, string>;
    icon?: string;
    handler: {
        type: 'callFrontendMethod' | 'callBackendEndpoint' | 'redirect';
        method?: string;
        url?: string;
        params?: any[];
        payload?: any;
        showLoading?: boolean;
        dismissOnSuccess?: boolean;
    };
}

export interface NotificationItem {
    id: string | number;
    title: string;
    message: string;
    type?: 'success' | 'error' | 'warning' | 'info' | 'modal';
    duration?: number; // ms
    priority?: 'low' | 'normal' | 'high' | 'urgent';
    persistent?: boolean;
    actions?: NotificationAction[];
    isDisplaying?: boolean;
    progress?: number;
    startTime?: number;
    identifier?: string;
    modalPopupId?: string;
    icon?: string;
    image?: string;
    suppressToast?: boolean;
}

export interface NotificationsState {
    queue: NotificationItem[];
    currentNotification: NotificationItem | null;
    isDisplaying: boolean;
    pauseAutoChaining?: boolean;
    isInboundPaused?: boolean;
    deferredQueue?: NotificationItem[];
}

const initialState: NotificationsState = {
    queue: [],
    currentNotification: null,
    isDisplaying: false,
    pauseAutoChaining: false,
    isInboundPaused: false,
    deferredQueue: [],
};

const notificationsSlice = createSlice({
    name: 'notifications',
    initialState,
    reducers: {
        addNotificationToQueue: (state, action: PayloadAction<{ notification: any; suppressImmediateDisplay?: boolean; }>) => {
            const { notification } = action.payload;

            const isModal = (notification.type === 'modal');

            const item: NotificationItem = {
                id: notification.id,
                title: notification.title || 'Notification',
                message: notification.message || notification.text || '',
                type: notification.type || 'info',
                duration: isModal ? undefined : (notification.duration || 5000),
                priority: notification.priority || 'normal',
                persistent: notification.persistent || false,
                actions: notification.actions || [],
                isDisplaying: false,
                progress: 0,
                startTime: undefined,
                identifier: notification.identifier || notification.modalId || undefined,
                modalPopupId: notification.modalPopupId || undefined,
                icon: notification.icon || undefined,
                suppressToast: notification.suppressToast || (isModal ? true : false),
                image: notification.image || undefined,
            };

            // If inbound is paused, buffer and return
            if (state.isInboundPaused) {
                if (item.type === 'modal') {
                    const existsInDeferred = (state.deferredQueue || []).some(n => n.type === 'modal' && n.id === item.id);
                    const isSameAsCurrent = !!(state.currentNotification && state.currentNotification.type === 'modal' && state.currentNotification.id === item.id);
                    const existsInQueue = state.queue.some(n => n.type === 'modal' && n.id === item.id);
                    if (existsInDeferred || isSameAsCurrent || existsInQueue) return;
                }
                (state.deferredQueue as NotificationItem[]).push(item);
                return;
            }

            if (isModal) {
                const sameIdAsCurrent = !!(state.currentNotification && state.currentNotification.type === 'modal' && state.currentNotification.id === item.id);
                const idInQueue = state.queue.some(n => n.type === 'modal' && n.id === item.id);
                if (sameIdAsCurrent || idInQueue) {
                    return;
                }
            }

            const priorityOrder = { urgent: 4, high: 3, normal: 2, low: 1 } as const;
            const newPriority = priorityOrder[item.priority as keyof typeof priorityOrder] || 2;

            const existingIndex = state.queue.findIndex((n) => n.id === item.id);
            if (existingIndex >= 0) {
                state.queue[existingIndex] = item;
            } else {
                let insertIndex = state.queue.length;
                for (let i = 0; i < state.queue.length; i++) {
                    const queuePriority = priorityOrder[state.queue[i].priority as keyof typeof priorityOrder] || 2;
                    if (newPriority > queuePriority) {
                        insertIndex = i;
                        break;
                    }
                }
                state.queue.splice(insertIndex, 0, item);
            }

            if (!state.isDisplaying && !state.currentNotification && !action.payload.suppressImmediateDisplay) {
                const next = state.queue.shift();
                if (next) {
                    const isModal = next.type === 'modal';
                    state.currentNotification = {
                        ...next,
                        isDisplaying: !isModal && !next.suppressToast,
                        startTime: isModal ? undefined : Date.now(),
                        progress: isModal ? 0 : 0,
                    };
                    state.isDisplaying = !isModal && !next.suppressToast;
                }
            }
        },

        showNextNotification: (state) => {
            if (state.queue.length > 0 && !state.isDisplaying) {
                const next = state.queue.shift();
                if (next) {
                    const isModal = next.type === 'modal';
                    state.currentNotification = {
                        ...next,
                        isDisplaying: !isModal && !next.suppressToast,
                        startTime: isModal ? undefined : Date.now(),
                        progress: isModal ? 0 : 0,
                    };
                    state.isDisplaying = !isModal && !next.suppressToast;
                }
            }
        },

        hideCurrentNotification: (state) => {
            state.currentNotification = null;
            state.isDisplaying = false;

            if (state.queue.length > 0) {
                const next = state.queue.shift();
                if (next) {
                    const isModal = next.type === 'modal';
                    state.currentNotification = {
                        ...next,
                        isDisplaying: !isModal && !next.suppressToast,
                        startTime: isModal ? undefined : Date.now(),
                        progress: isModal ? 0 : 0,
                    };
                    state.isDisplaying = !isModal && !next.suppressToast;
                }
            }
        },

        updateNotificationProgress: (state, action: PayloadAction<{ progress: number; }>) => {
            if (state.currentNotification) {
                state.currentNotification.progress = action.payload.progress;
            }
        },

        calculateNotificationProgress: (state) => {
            if (state.currentNotification && state.currentNotification.startTime && state.isDisplaying) {
                const now = Date.now();
                const elapsed = now - state.currentNotification.startTime;
                const duration = state.currentNotification.duration || 5000;
                const progress = Math.min(100, (elapsed / duration) * 100);

                state.currentNotification.progress = progress;

                if (progress >= 100 && !state.currentNotification.persistent) {
                    state.currentNotification = null;
                    state.isDisplaying = false;

                    if (state.queue.length > 0 && !state.pauseAutoChaining) {
                        const next = state.queue.shift();
                        if (next) {
                            const isModal = next.type === 'modal';
                            state.currentNotification = {
                                ...next,
                                isDisplaying: !isModal && !next.suppressToast,
                                startTime: isModal ? undefined : Date.now(),
                                progress: isModal ? 0 : 0,
                            };
                            state.isDisplaying = !isModal && !next.suppressToast;
                        }
                    }
                } else if (progress >= 100 && state.currentNotification.persistent) {
                    state.currentNotification.progress = 100;
                }
            }
        },

        restartNotificationTimer: (state) => {
            if (state.currentNotification && state.isDisplaying) {
                state.currentNotification.startTime = Date.now();
                state.currentNotification.progress = 0;
            }
        },

        removeNotificationFromQueue: (state, action: PayloadAction<{ id: string | number; }>) => {
            const { id } = action.payload;
            state.queue = state.queue.filter((n) => n.id !== id);
        },

        clearAllNotifications: (state) => {
            state.queue = [];
            state.currentNotification = null;
            state.isDisplaying = false;
            state.deferredQueue = [];
        },

        setToastAutoChainPaused: (state, action: PayloadAction<boolean>) => {
            state.pauseAutoChaining = action.payload;
        },

        pauseInboundNotifications: (state) => {
            state.isInboundPaused = true;
        },
        resumeInboundNotifications: (state) => {
            state.isInboundPaused = false;
            const buffered = (state.deferredQueue || []).splice(0) as NotificationItem[];
            if (buffered && buffered.length) {
                // Re-enqueue buffered items respecting priority and modal de-dupe
                const priorityOrder = { urgent: 4, high: 3, normal: 2, low: 1 } as const;
                for (const item of buffered) {
                    if (item.type === 'modal') {
                        const sameIdAsCurrent = !!(state.currentNotification && state.currentNotification.type === 'modal' && state.currentNotification.id === item.id);
                        const idInQueue = state.queue.some(n => n.type === 'modal' && n.id === item.id);
                        if (sameIdAsCurrent || idInQueue) {
                            continue;
                        }
                    }
                    const newPriority = priorityOrder[item.priority as keyof typeof priorityOrder] || 2;
                    let insertIndex = state.queue.length;
                    for (let i = 0; i < state.queue.length; i++) {
                        const queuePriority = priorityOrder[state.queue[i].priority as keyof typeof priorityOrder] || 2;
                        if (newPriority > queuePriority) {
                            insertIndex = i;
                            break;
                        }
                    }
                    state.queue.splice(insertIndex, 0, item);
                }
                // Start displaying if idle
                if (!state.isDisplaying && !state.currentNotification) {
                    const next = state.queue.shift();
                    if (next) {
                        const isModal = next.type === 'modal';
                        state.currentNotification = {
                            ...next,
                            isDisplaying: !isModal && !next.suppressToast,
                            startTime: isModal ? undefined : Date.now(),
                            progress: isModal ? 0 : 0,
                        };
                        state.isDisplaying = !isModal && !next.suppressToast;
                    }
                }
            }
        },
    },
});

export const {
    addNotificationToQueue,
    showNextNotification,
    hideCurrentNotification,
    updateNotificationProgress,
    calculateNotificationProgress,
    restartNotificationTimer,
    removeNotificationFromQueue,
    clearAllNotifications,
    setToastAutoChainPaused,
    pauseInboundNotifications,
    resumeInboundNotifications,
} = notificationsSlice.actions;

// Selectors
export const selectNotificationQueue = (state: RootState) => state.notifications.queue;
export const selectCurrentNotification = (state: RootState) => state.notifications.currentNotification;
export const selectIsNotificationDisplaying = (state: RootState) => state.notifications.isDisplaying;
export const selectNotificationQueueLength = (state: RootState) => state.notifications.queue.length;
export const selectShouldSuppressToast = (state: RootState) => !!state.notifications.currentNotification?.suppressToast;

export default notificationsSlice.reducer;
